/**
 * AppPromotionCard
 * Reusable contextual app promotion card.
 *
 * variant: 'footer' | 'inline' | 'post_gold' | 'collection' | 'watchlist' | 'share_page'
 *
 */
import AppDownloadButton from '../AppDownloadButton/AppDownloadButton';
import styles from './AppPromotionCard.module.css';

const COPY = {
    footer: {
        title: 'Take Aluvo with you',
        description: 'Get reminders for Legendary attempts, collection alerts, and watchlist updates.',
        ctaLabel: null, // handled by AppDownloadButton defaults
    },
    inline: {
        title: 'Take Aluvo with you',
        description:
            'Aluvo works great on web, but the app keeps your progress closer. Get Legendary attempt reminders, collection alerts, watchlist updates, and faster coin sharing from your phone.',
        ctaLabel: null,
    },
    post_gold: {
        title: 'Your Legendary attempt opens in 24 hours',
        description: "Get the app and we'll remind you when it's ready.",
        ctaLabel: 'Notify Me in the App',
    },
    collection: {
        title: "You're close to unlocking your next reward",
        description:
            'Get the app for collection alerts and never miss a reward coin opportunity.',
        ctaLabel: 'Get Collection Alerts',
    },
    watchlist: {
        title: 'Know when it\'s ready to verify',
        description:
            'Get alerts when movies and seasons on your watchlist are ready for a challenge.',
        ctaLabel: 'Get Watchlist Alerts',
    },
    share_page: {
        title: 'Build your own verified movie profile',
        description:
            'Earn coins, unlock collections, and get reminders when Legendary attempts are ready.',
        ctaLabel: null,
    },
};

const BENEFITS = [
    'Legendary attempt reminders',
    'Collection alerts',
    'Watchlist updates',
    'Faster coin sharing',
    'Quick access after watching',
];

function AppPromotionCard({
                              variant = 'inline',
                              /** Override headline */
                              title,
                              /** Override body */
                              description,
                              /** Pass a reward coin name for collection variant */
                              rewardCoinName,
                              compact = false,
                              showBenefits = false,
                              className = '',
                          }) {
    const copy = COPY[variant] || COPY.inline;

    const resolvedTitle =
        title ??
        (rewardCoinName && variant === 'collection'
            ? `You're 1 title away from ${rewardCoinName}`
            : copy.title);

    const resolvedDescription = description ?? copy.description;

    return (
        <div className={`${styles.card} ${styles[variant]} ${compact ? styles.compact : ''} ${className}`}>
            <div className={styles.iconRow}>
                <span className={styles.appIcon}>📱</span>
                {variant === 'post_gold' && <span className={styles.glowDot} />}
            </div>

            <div className={styles.body}>
                <h3 className={styles.title}>{resolvedTitle}</h3>
                <p className={styles.description}>{resolvedDescription}</p>

                {showBenefits && !compact && (
                    <ul className={styles.benefits}>
                        {BENEFITS.map((b) => (
                            <li key={b} className={styles.benefit}>
                                <span className={styles.benefitDot}>◈</span>
                                {b}
                            </li>
                        ))}
                    </ul>
                )}

                <div className={styles.cta}>
                    <AppDownloadButton
                        source={variant}
                        labelOverride={copy.ctaLabel ?? undefined}
                        size={compact ? 'sm' : 'md'}
                    />
                </div>
            </div>
        </div>
    );
}

export default AppPromotionCard;
